import { useContext } from 'react'
import {
  AgxSolicitorDetails,
  AgxVendorBuyerDetails,
  EditScope as LegacyEditScope,
  StakeholderType as LegacyStakeholderType,
  VendorBuyerType as LegacyVendorBuyerType,
} from '@urbanx/agx-ui-components'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgxAlternateAddressSearch,
  AgxSpacer,
  AgxTypography,
  AgxVendorBuyers,
} from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import { useFeatureToggles } from 'hooks'
import { deleteFile, getFileLink, uploadFile } from 'services'
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  EditScope,
  SolicitorDetails,
  StakeholderType,
  VendorBuyerTypes,
  VendorModel,
} from '@urbanx/agx.campaigns.client'

interface VendorDetailsSectionProps {
  campaign?: CampaignDetailModel | undefined
  vendorDetails: VendorModel[]
  setVendorDetails: (value: VendorModel[]) => void
  solicitorDetails: SolicitorDetails | undefined
  setSolicitorDetails: (value: SolicitorDetails | undefined) => void
  hasUnsavedChanges: boolean
}

const VendorDetailsSection = ({
  campaign,
  vendorDetails,
  setVendorDetails,
  solicitorDetails,
  setSolicitorDetails,
  hasUnsavedChanges,
}: VendorDetailsSectionProps) => {
  const { featureToggles } = useFeatureToggles()
  const { getAuthToken } = useContext(AuthContext)

  return (
    <AgxSpacer id="vendor-details" direction="vertical" gap="spacing-xl" fill>
      <Panel
        title={
          <AgxTypography id="vendorDetailsTitle" type="body" size="medium">
            Vendor Details
          </AgxTypography>
        }
        padding={{
          top: 'md',
          bottom: 'md',
          left: 'xl',
          right: 'xl',
        }}
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          {featureToggles?.identityVerification === true ? (
            <AgxVendorBuyers
              key={`VendorDetails-${vendorDetails.length}`}
              id="vendorDetails"
              onChange={({ value }) => setVendorDetails(value as VendorModel[])}
              propertyAddress={campaign?.address}
              defaultValue={vendorDetails}
              editScope={EditScope.FullEdit}
              stakeholderType={StakeholderType.Vendor}
              vendorBuyerTypes={[
                VendorBuyerTypes.Individual,
                VendorBuyerTypes.PowerOfAttorney,
                VendorBuyerTypes.DeceasedEstate,
                VendorBuyerTypes.Company,
              ]}
              companyVendorBuyerTypes={[
                VendorBuyerTypes.Individual,
                VendorBuyerTypes.PowerOfAttorney,
                VendorBuyerTypes.DeceasedEstate,
                VendorBuyerTypes.Company,
              ]}
              vendorBuyerConfig={{
                preferredNameRequired: true,
                emailRequired: true,
                phoneNumberRequired: true,
                addressRequired: false,
                excludeMiddleName: false,
                displayAddCc: false,
                hideIdentityVerification: true,
              }}
              powerOfAttorneyConfig={{
                hideDocumentationUpload: true,
              }}
              companyConfig={{
                hideAbn: false,
                hideAcn: false,
                hideRegisteredForGst: false,
                requireAcnOrAbn:
                  campaign?.state === CampaignDetailModelState.NSW,
              }}
              required
              campaignId={campaign?.campaignId || ''}
              displayLegalName={false}
              agents={[]}
              leadAgentId=""
              validate={hasUnsavedChanges}
            />
          ) : (
            <AgxVendorBuyerDetails
              key={`VendorDetails-${vendorDetails.length}`}
              id="vendorDetails"
              getAuthToken={getAuthToken}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              onValueChanged={({ value }) =>
                setVendorDetails((value ?? []) as VendorModel[])
              }
              propertyAddress={campaign?.address}
              defaultValue={vendorDetails}
              editScope={LegacyEditScope.FullEdit}
              stakeholderType={LegacyStakeholderType.Vendor}
              vendorBuyerTypes={[
                LegacyVendorBuyerType.Individual,
                LegacyVendorBuyerType.PowerOfAttorney,
                LegacyVendorBuyerType.DeceasedEstate,
                LegacyVendorBuyerType.Company,
              ]}
              companyVendorBuyerTypes={[
                LegacyVendorBuyerType.Individual,
                LegacyVendorBuyerType.PowerOfAttorney,
                LegacyVendorBuyerType.DeceasedEstate,
                LegacyVendorBuyerType.Company,
              ]}
              vendorBuyerConfig={{
                preferredNameRequired: true,
                emailRequired: true,
                phoneNumberRequired: true,
                addressRequired: false,
                excludeMiddleName: false,
                displayAddCc: false,
              }}
              powerOfAttorneyConfig={{
                hideDocumentationUpload: true,
              }}
              companyConfig={{
                hideAbn: false,
                hideAcn: false,
                hideRegisteredForGst: false,
                requireAcnOrAbn:
                  campaign?.state === CampaignDetailModelState.NSW,
              }}
              required
              campaignId={campaign?.campaignId || ''}
              displayLegalName={false}
              agents={[]}
              leadAgentId=""
              validate={hasUnsavedChanges}
              alternateAddressSearchComponent={AgxAlternateAddressSearch}
            />
          )}
        </AgxSpacer>
      </Panel>
      <Panel
        title={
          <AgxTypography id="vendorSolicitorTitle" type="body" size="medium">
            Vendor Solicitor
          </AgxTypography>
        }
        padding={{
          top: 'md',
          bottom: 'md',
          left: 'xl',
          right: 'xl',
        }}
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxSolicitorDetails
            key={`VendorSolicitorDetails-${solicitorDetails?.solicitorType}`}
            id="vendorSolicitorDetails"
            onValueUpdated={({ value }) => setSolicitorDetails(value)}
            defaultValue={solicitorDetails}
            label=""
            solicitorLabel="Solicitor"
            showToBeAdvisedOption
            required
            validate={hasUnsavedChanges}
          />
        </AgxSpacer>
      </Panel>
    </AgxSpacer>
  )
}

export default VendorDetailsSection
